import { useSelector } from "react-redux";
import { useRouter } from "next/router";

import {
  createReactivationSchedulers,
  isAccountPaused,
  isGroceryBasketType,
  isMealboxBasketType,
} from "@chef/helpers";
import {
  ACCOUNT_STATUSES,
  BRAND_NAME,
  BRAND_PATHS,
  CONSENT_IDS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import {
  selectDeviationBasket,
  showNotification,
  useBillingQuery,
  useCreateOrUpdateDeviationByBasketIdMutation,
  useCreateOrUpdateMultipleSchedulersMutation,
  usePickAndMixQuery,
  useUpdateBillingAgreementMutation,
  useValidateBasketMutation,
  useCreateOrUpdateSchedulerMutation,
} from "@chef/state-management";
import { isEqualStrings } from "@chef/utils/equal";
import {
  useCalendarData,
  usePreselector,
  useTrack,
} from "@chef/state-management/hooks";
import {
  compareBasketsProducts,
  getBasketFinances,
  getBasketForCalendarWeek,
  getCurrentWeekDataAndBasketFromCalendar,
  getWeeklyTimeblockDataFromCalendar,
} from "@chef/state-management/helpers";
import { useDebounce } from "@chef/hooks";
import { useLoyalty } from "@chef/state-management/hooks";
import { isEmptyArray, sumByProperty } from "@chef/utils/array";

import type { WeekYear } from "@chef/types/common";

import { filterBasketItems } from "../../utils";

import { intl } from "./WeekEditorConfirmOrderExperimental.Intl";

export const useSubmitOrder = ({ week, year }: WeekYear) => {
  const router = useRouter();
  const track = useTrack();

  const { trigger } = usePreselector();

  const deviationBasketForCurrentWeekYear = useSelector(
    selectDeviationBasket({ week, year }),
  );

  const deviationProductsForCurrentWeekYear =
    deviationBasketForCurrentWeekYear?.products;

  const [
    createOrUpdateDeviationByBasketId,
    { isLoading: isLoadingCreateOrUpdateDeviation },
  ] = useCreateOrUpdateDeviationByBasketIdMutation();

  const [
    createOrUpdateMultipleSchedulersMutation,
    { isLoading: isLoadingCreateOrUpdateMultipleSchedulers },
  ] = useCreateOrUpdateMultipleSchedulersMutation();

  const [validateBasketMutation, { isLoading: isLoadingValidateBasket }] =
    useValidateBasketMutation();

  const [
    updateBillingAgreementMutation,
    { isLoading: isLoadingUpdateBillingAgreementMutation },
  ] = useUpdateBillingAgreementMutation();

  const [createOrUpdateScheduler] = useCreateOrUpdateSchedulerMutation();

  const isConfirmOrderLoading =
    isLoadingUpdateBillingAgreementMutation ||
    isLoadingCreateOrUpdateDeviation ||
    isLoadingCreateOrUpdateMultipleSchedulers ||
    isLoadingValidateBasket;

  const isSubmitOrderLoading =
    useDebounce(isConfirmOrderLoading, 50) || isConfirmOrderLoading;

  const { data: billingData } = useBillingQuery();

  const { data: loyaltyData } = useLoyalty();

  const { basket, calendar } = useCalendarData({
    week,
    year,
  });

  const { data: { pickAndMix = [] } = {} } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const calendarWeek = calendar?.filter(
    (c) => c.week === week && c.year === year,
  );

  const timeblock =
    calendarWeek &&
    getWeeklyTimeblockDataFromCalendar({
      calendar: calendarWeek,
      week,
      year,
    });

  const finances =
    deviationProductsForCurrentWeekYear &&
    getBasketFinances({
      basketProducts: deviationProductsForCurrentWeekYear,
      customerFee: timeblock?.customerFee,
      loyaltyLevel: loyaltyData?.currentLevel,
    });

  const calendarMealboxBaskets = calendar?.map(
    (c) =>
      getBasketForCalendarWeek({
        calendarWeek: c,
      }).mealboxBasket?.basketDetails.products,
  );

  const filteredCalendarMealboxBaskets = calendarMealboxBaskets?.map(
    (calendarMealboxBasket) =>
      calendarMealboxBasket?.filter((product) =>
        isEqualStrings(
          product.variation.product.productTypeId,
          PRODUCT_TYPE_IDS.PICKANDMIX,
        ),
      ),
  );

  const isSomeUpcomingDeviationEmpty = filteredCalendarMealboxBaskets?.some(
    (udm) => udm && isEmptyArray(udm),
  );

  const handleSubmitOrder = async () => {
    if (
      !deviationProductsForCurrentWeekYear ||
      !pickAndMix ||
      !billingData?.billing
    ) {
      throw new Error("Data not initialized");
    }

    const calendarWeekData =
      calendarWeek &&
      getCurrentWeekDataAndBasketFromCalendar({
        calendar: calendarWeek,
        week,
        year,
      });

    const isBeforeStartDateWeek = calendarWeekData?.isBeforeStartDateWeek;

    if (isBeforeStartDateWeek) {
      showNotification({
        type: "error",
        message: intl.NO_DELIVERIES_BEFORE_START_WEEK,
      });
      router.push(BRAND_PATHS.MY_MENU_PAGE);
      return;
    }

    await validateBasketMutation(
      deviationProductsForCurrentWeekYear.map((bp) => ({
        quantity: bp.quantity,
        variationId: bp.variationId,
      })),
    ).unwrap();

    const isPausedAccount = isAccountPaused(billingData.billing.status || 10);

    if (isPausedAccount) {
      await updateBillingAgreementMutation({
        status: ACCOUNT_STATUSES.ACTIVE,
        _suppressNotification: true,
      }).unwrap();

      await createOrUpdateMultipleSchedulersMutation({
        schedulers: createReactivationSchedulers(week, year),
        _suppressNotifications: true,
      }).unwrap();
    }

    const { mealboxBasketItems, groceryBasketItems } = filterBasketItems(
      deviationProductsForCurrentWeekYear,
    );

    const groceryBasketId =
      billingData.billing.baskets.find(isGroceryBasketType)?.basketId;

    const weekIsActive =
      calendarWeekData?.mealboxBasket?.shippingDetails.hasDelivery;

    const mealboxBasketId =
      billingData.billing.baskets.find(isMealboxBasketType)?.basketId;

    const isMealboxBasketChanged = !compareBasketsProducts(
      basket.mealboxBasket,
      mealboxBasketItems,
    );

    const isGroceryBasketChanged = !compareBasketsProducts(
      basket.groceryBasket,
      groceryBasketItems,
    );

    if (mealboxBasketId && isMealboxBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: mealboxBasketId,
        week,
        year,
        basketDeviationProducts: mealboxBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    if (groceryBasketId && isGroceryBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: groceryBasketId,
        week,
        year,
        basketDeviationProducts: groceryBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    track("deviationUpdateStepCompleted", {
      affiliation: "Frontend process",
      delivery_update_step: 3,
      delivery_update_step_name: "userConfirmation",
      delivery_update_step_title: intl.ORDER,
    });

    track("deviationCreated", {
      affiliation: "Frontend process",
      source: "week_editor",
      is_active: true,
      products: deviationProductsForCurrentWeekYear.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      quantity: sumByProperty(deviationProductsForCurrentWeekYear, "quantity"),
      total: finances?.total,
      status: billingData.billing.status,
      week,
      year,
    });

    if (!isMealboxBasketChanged && !weekIsActive) {
      await createOrUpdateScheduler({ week, year, delivery: true }).unwrap();
    }

    if (isPausedAccount) {
      const hasAllergenPreferences = billingData?.billing.preferences.some(
        (p) => p.isAllergen,
      );

      const requiredConsentIds: string[] = [
        CONSENT_IDS.DATA_PROCESSING[BRAND_NAME],
      ];

      if (hasAllergenPreferences) {
        requiredConsentIds.push(CONSENT_IDS.ALLERGEN_STORING[BRAND_NAME]);
      }
      showNotification({
        type: "success",
        message: intl.NOTIFICATION_DELIVERY_STARTED,
      });
      if (isSomeUpcomingDeviationEmpty) {
        trigger({
          week,
          year,
          requiredConsentIds,
          overrideDeviations: false,
        });
      }
      router.push(BRAND_PATHS.RESTART_BILLING({ week, year }));
    } else {
      router.push(BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }));
    }
  };

  return {
    handleSubmitOrder,
    isSubmitOrderLoading,
  };
};
